import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import AppSidebar from './AppSidebar';
import MobileBottomNav from './MobileBottomNav';
import GlobalSearch from './GlobalSearch';
import NotificationBell from './NotificationBell';
import { ResponsiveLogo } from './ThemeLogo';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import {
  Moon,
  Sun,
  LogOut,
  User,
  Plug,
  KeyRound,
  HelpCircle,
  Crown,
  Shield,
  Quote,
} from 'lucide-react';

const STRATEGY_QUOTES = [
  { text: "The difference between a dream and a goal is a plan.", author: "Steve Harvey" },
  { text: "Strategy without tactics is the slowest route to victory. Tactics without strategy is the noise before defeat.", author: "Sun Tzu" },
  { text: "Vision without execution is hallucination.", author: "Thomas Edison" },
  { text: "The essence of strategy is choosing what not to do.", author: "Michael Porter" },
  { text: "If you don't know where you are going, any road will get you there.", author: "Lewis Carroll" },
  { text: "Plans are nothing; planning is everything.", author: "Dwight D. Eisenhower" },
  { text: "Good business leaders create a vision, articulate the vision, and relentlessly drive it to completion.", author: "Jack Welch" },
  { text: "However beautiful the strategy, you should occasionally look at the results.", author: "Winston Churchill" },
  { text: "Execution is a systematic process of rigorously discussing hows and whats.", author: "Larry Bossidy" },
  { text: "What gets measured gets managed.", author: "Peter Drucker" },
  { text: "Culture eats strategy for breakfast.", author: "Peter Drucker" },
  { text: "The best time to plant a tree was 20 years ago. The second best time is now.", author: "Chinese Proverb" },
  { text: "In preparing for battle I have always found that plans are useless, but planning is indispensable.", author: "Dwight D. Eisenhower" },
  { text: "Move fast and break things. Unless you are breaking stuff, you are not moving fast enough.", author: "Mark Zuckerberg" },
  { text: "Ideas are easy. Implementation is hard.", author: "Guy Kawasaki" },
  { text: "Do not wait to strike till the iron is hot, but make it hot by striking.", author: "William Butler Yeats" },
];

const getDailyQuote = () => {
  const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0)) / 86400000);
  return STRATEGY_QUOTES[dayOfYear % STRATEGY_QUOTES.length];
};

const getInitials = (name) => {
  return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
};

export default function AppLayout({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('odapto-sidebar-collapsed');
    return saved === 'true';
  });

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('odapto-sidebar-collapsed', String(next));
      return next;
    });
  };

  const handleLogout = async () => {
    await logout();
  };

  const quote = getDailyQuote();

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar — hidden on mobile */}
      <div className="hidden md:block">
        <AppSidebar
          collapsed={sidebarCollapsed}
          onToggle={toggleSidebar}
        />
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Navigation */}
        <nav className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border" data-testid="app-top-nav">
          <div className="px-4 md:px-6">
            <div className="flex items-center justify-between h-14">
              {/* Mobile: Logo | Desktop: Search */}
              <div className="md:hidden flex items-center">
                <Link to="/dashboard">
                  <ResponsiveLogo className="h-8 w-auto" />
                </Link>
              </div>
              <div className="hidden md:block">
                <GlobalSearch />
              </div>

              {/* Right side controls */}
              <div className="flex items-center gap-2 ml-auto">
                <NotificationBell />

                <button
                  onClick={toggleTheme}
                  className="p-2 rounded-full hover:bg-muted transition-colors"
                  data-testid="theme-toggle"
                >
                  {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                </button>

                {/* Avatar — hidden on mobile (profile in bottom nav) */}
                <div className="hidden md:block">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="flex items-center gap-2" data-testid="user-menu-btn">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={user?.picture} alt={user?.name} />
                          <AvatarFallback className="bg-odapto-orange text-white text-sm">
                            {getInitials(user?.name)}
                          </AvatarFallback>
                        </Avatar>
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <div className="px-3 py-2 border-b border-border">
                        <p className="font-medium">{user?.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                      </div>
                      <DropdownMenuItem asChild>
                        <Link to="/profile" className="cursor-pointer">
                          <User className="w-4 h-4 mr-2" />
                          Profile
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/integrations" className="cursor-pointer">
                          <Plug className="w-4 h-4 mr-2" />
                          Integrations
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/profile?tab=password" className="cursor-pointer">
                          <KeyRound className="w-4 h-4 mr-2" />
                          Change Password
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/help" className="cursor-pointer">
                          <HelpCircle className="w-4 h-4 mr-2" />
                          Help
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/upgrade" className="cursor-pointer text-odapto-orange">
                          <Crown className="w-4 h-4 mr-2" />
                          Upgrade Plan
                        </Link>
                      </DropdownMenuItem>
                      {isAdmin && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem asChild>
                            <Link to="/admin" className="cursor-pointer">
                              <Shield className="w-4 h-4 mr-2" />
                              Admin Panel
                            </Link>
                          </DropdownMenuItem>
                        </>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleLogout} className="text-destructive cursor-pointer" data-testid="logout-btn">
                        <LogOut className="w-4 h-4 mr-2" />
                        Log out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>
          </div>
        </nav>

        {/* Page Content — mobile has bottom padding for nav */}
        <div className="flex-1 overflow-auto pb-24 md:pb-0">
          {children}
        </div>

        {/* Quote Bar Footer — hidden on mobile */}
        <div className="hidden md:block shrink-0 border-t border-border bg-muted/50" data-testid="quote-bar">
          <div className="flex items-center gap-3 px-5 py-2">
            <Quote className="w-4 h-4 text-amber-500 shrink-0" />
            <p className="text-sm text-muted-foreground italic font-light tracking-wide">
              "{quote.text}"
            </p>
            <span className="text-xs text-muted-foreground/60 shrink-0 ml-auto">— {quote.author}</span>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
}
